import { useNavigate } from "react-router-dom";
import { Globe2, MapPin } from "lucide-react";

const CONTINENTS = [
  { d: "M55,120 Q70,60 140,45 Q210,35 255,70 Q270,100 250,130 Q230,155 205,165 Q185,205 160,238 Q130,235 115,195 Q80,170 55,120 Z", fill: "url(#gPink)" },
  { d: "M232,268 Q268,246 292,276 Q304,326 288,378 Q272,428 254,440 Q238,398 232,348 Q222,300 232,268 Z", fill: "url(#gOrange)" },
  { d: "M455,175 Q510,152 556,180 Q578,218 566,258 Q556,300 536,340 Q520,376 504,382 Q482,340 472,298 Q450,245 455,175 Z", fill: "url(#gAmber)" },
  { d: "M448,78 Q495,55 548,70 Q572,92 562,118 Q532,142 494,152 Q464,132 448,78 Z", fill: "url(#gTeal)" },
  { d: "M575,58 Q680,35 785,68 Q852,88 862,128 Q835,168 792,180 Q752,212 700,235 Q670,268 655,285 Q638,255 640,220 Q605,175 585,130 Q572,95 575,58 Z", fill: "url(#gPurple)" },
  { d: "M855,128 Q878,118 890,140 Q885,165 868,172 Q852,155 855,128 Z", fill: "url(#gPurple)" },
  { d: "M785,330 Q838,312 872,340 Q878,376 846,396 Q804,402 784,376 Q775,350 785,330 Z", fill: "url(#gBlue)" },
];

export const WorldMapSection = ({ countries = [] }) => {
  const navigate = useNavigate();
  const stats = [
    ["195+", "Countries"],
    ["3,000+", "Music Cultures"],
    ["1 World", "A Million Sounds"],
  ];

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 md:p-6 h-full flex flex-col" data-testid="world-map-section">
      <div className="flex items-start gap-3 mb-4">
        <span className="w-9 h-9 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center shrink-0"><Globe2 className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /></span>
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-black text-slate-900">Music Around the World</h2>
          <p className="text-xs text-slate-500">Explore a global map of music. Click a country to discover its unique sounds, artists and traditions.</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 items-center flex-1">
        <div className="flex flex-col gap-5 col-span-4 sm:col-span-1 flex-row sm:flex-col">
          {stats.map(([n, l]) => (
            <div key={l}>
              <p className="font-display text-2xl lg:text-3xl font-black gradient-text">{n}</p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{l}</p>
            </div>
          ))}
          <p className="hidden sm:flex items-center gap-1.5 text-[11px] font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 w-fit">
            <MapPin className="w-3 h-3 text-pink-500" /> Explore local sounds, global connections
          </p>
        </div>

        <div className="col-span-4 sm:col-span-3">
          <svg viewBox="0 0 940 470" className="w-full h-auto" role="img" aria-label="Stylized world music map">
            <defs>
              <linearGradient id="gPink" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#F9A8D4" /><stop offset="100%" stopColor="#C084FC" /></linearGradient>
              <linearGradient id="gOrange" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#FDBA74" /><stop offset="100%" stopColor="#F472B6" /></linearGradient>
              <linearGradient id="gAmber" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#FCD34D" /><stop offset="100%" stopColor="#FB923C" /></linearGradient>
              <linearGradient id="gTeal" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#5EEAD4" /><stop offset="100%" stopColor="#818CF8" /></linearGradient>
              <linearGradient id="gPurple" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#C4B5FD" /><stop offset="100%" stopColor="#F472B6" /></linearGradient>
              <linearGradient id="gBlue" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#93C5FD" /><stop offset="100%" stopColor="#A78BFA" /></linearGradient>
            </defs>
            {CONTINENTS.map((c, i) => <path key={i} d={c.d} fill={c.fill} opacity="0.9" />)}
            {countries.filter((c) => c.hotspot).map((c) => (
              <g key={c.slug} data-testid={`map-hotspot-${c.slug}`} className="cursor-pointer" onClick={() => navigate(`/places/${c.slug}`)}>
                <circle cx={c.hotspot.x} cy={c.hotspot.y} r="9" fill="#EC4899" opacity="0.35" className="map-pulse" />
                <circle cx={c.hotspot.x} cy={c.hotspot.y} r="5.5" fill="#fff" stroke="#DB2777" strokeWidth="3" />
                <title>{`${c.name} — explore its music`}</title>
              </g>
            ))}
          </svg>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {countries.map((c) => (
          <button
            key={c.slug}
            data-testid={`map-country-pill-${c.slug}`}
            onClick={() => navigate(`/places/${c.slug}`)}
            className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-[11px] font-bold text-slate-700 hover:border-pink-400 hover:text-pink-600 hover:bg-pink-50 transition-colors"
          >
            {c.flag} {c.name}
          </button>
        ))}
      </div>
    </div>
  );
};

export default WorldMapSection;
